import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Slot } from "./Slot";
import { pointInQuad, projectedSheetQuad } from "./projection";
// Stable empty defaults so a slot that has no photos / cells / resources gets
// the same prop identity on every render — otherwise `?? []`/`?? {}` would mint
// a fresh object each render and defeat Slot's React.memo.
const EMPTY_URLS = [];
const EMPTY_CELL_TAGS = [];
const EMPTY_RESOURCES = {};
const FULL_CELL = [[0, 0, 1, 1]];
// Font stack pinned so the canvas-measured text width matches what the page
// actually paints. Pick a sans-serif that's consistently shaped across macOS,
// Windows, and Linux without pulling in a custom font.
const HOVER_FONT_FAMILY = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
// Horizontal inset that the hovered group (slot + label) must stay inside,
// expressed as a fraction of viewport HEIGHT, per the design spec.
const HOVER_MARGIN_OF_VH = 0.025;
// Hover label font sizes as a fraction of viewport HEIGHT. The secondary
// line (template name) is one step smaller than the primary (product name)
// line; its weight is 100 lighter (see the render below). Tune here.
const HOVER_LABEL_FONT_OF_VH = 0.04;
const HOVER_SUBLABEL_FONT_OF_VH = 0.032;
// Vertical gap between the bottom of the hovered slot and the top of the
// first label line, as a fraction of viewport HEIGHT.
const HOVER_LABEL_GAP_OF_VH = 0.02;
// Internal padding of the white pill behind each label line, as a fraction
// of viewport HEIGHT. Horizontal is larger than vertical so the capsule has
// room either side of the text; tune freely.
const HOVER_LABEL_PAD_Y_OF_VH = 0.01;
const HOVER_LABEL_PAD_X_OF_VH = 0.03;
// Opacity of the white pill behind each label line: 0 = invisible, 1 = solid
// white. Lower it for a translucent capsule that lets the photo show through.
const HOVER_LABEL_BG_ALPHA = 0.8;
const HOVER_LABEL_BG = `rgba(255, 255, 255, ${HOVER_LABEL_BG_ALPHA})`;
// White halo behind the label glyphs (stacked text-shadows) so the text
// stays legible over any background or photo. Shared by both lines.
const HOVER_LABEL_HALO = "0 0 4px #fff, 0 0 4px #fff, 0 0 4px #fff, 0 0 4px #fff";
// Canvas reused across measurements; instantiated lazily on first call.
let textMeasureCanvas = null;
function measureTextWidth(text, fontSpec) {
    if (typeof document === "undefined" || !text)
        return 0;
    if (!textMeasureCanvas) {
        textMeasureCanvas = document.createElement("canvas");
    }
    const ctx = textMeasureCanvas.getContext("2d");
    if (!ctx)
        return 0;
    ctx.font = fontSpec;
    return ctx.measureText(text).width;
}
const HOVER_TRANSITION_MS = 200;
// Top-level renderer. The root is a plain <div> sized to the layout's
// design viewport (e.g. 1184×1184), CSS-scaled to fit the available width
// via an outer wrapper. Each slot inside is a <div> with CSS transforms —
// CSS perspective + rotateX/Y/Z handles 3D lean natively, matching what
// the cyan selection overlay computes via projectedSheetQuad.
export function PackageGraphics({ data, className, selectedProductId, selectedDecorationId, interactiveDecorations, overlay, enableHover, }) {
    var _a, _b;
    const [hoveredProductId, setHoveredProductId] = useState(null);
    // Root element ref, used to map pointer client coords into design-viewport
    // coords for hover hit-testing (see handleHoverMove).
    const rootRef = useRef(null);
    // Tracks which slot has the *scaled* transform applied. Updated one
    // animation frame after hoveredProductId changes so the DOM reorders
    // commit first and the browser sees opacity / transform changes at a
    // stable DOM position. Without this two-step, CSS transition state gets
    // reset whenever React's reconciler does an explicit DOM move via
    // insertBefore / appendChild.
    const [animatedHoverIndex, setAnimatedHoverIndex] = useState(null);
    useLayoutEffect(() => {
        if (hoveredProductId === animatedHoverIndex)
            return;
        const raf = requestAnimationFrame(() => {
            setAnimatedHoverIndex(hoveredProductId);
        });
        return () => cancelAnimationFrame(raf);
    }, [hoveredProductId, animatedHoverIndex]);
    // "Sticky" version of hoveredProductId that holds the last non-null
    // hover and only clears after the fade-out transition completes. Drives
    // the label's content + position so the text doesn't go blank or slide
    // to the viewport centre while the opacity is interpolating from 1 → 0.
    const [stickyHoverIndex, setStickyHoverIndex] = useState(null);
    useLayoutEffect(() => {
        if (hoveredProductId !== null) {
            setStickyHoverIndex(hoveredProductId);
            return;
        }
        const timeout = window.setTimeout(() => {
            setStickyHoverIndex(null);
        }, HOVER_TRANSITION_MS + 50);
        return () => window.clearTimeout(timeout);
    }, [hoveredProductId]);
    const { layout, photoUrlsById, cellsByTemplateUuid, cellTagsByTemplateUuid, templateUserResourcesById, labelsById, templateLabelsById, placeholders, } = data;
    const vw = layout.viewport.width;
    const vh = layout.viewport.height;
    const bg = layout.background;
    const activeHover = enableHover && hoveredProductId !== null
        ? ((_a = layout.slots.find((s) => s.ref.byPackageProductId === hoveredProductId)) !== null && _a !== void 0 ? _a : null)
        : null;
    // Per-slot projected AABB. Used for the hover scale geometry (logical
    // slot.width/height differs from the visible extent for perspective-
    // projected slots) and as the transform-origin so the hover scale
    // doesn't drift the slot sideways. projectedSheetQuad gives the same
    // screen-coords here that CSS computes for the actual rendered slot —
    // we use it offline for layout decisions; the slot itself uses CSS 3D
    // for the actual transform.
    const visualBoundsByProductId = useMemo(() => {
        const m = new Map();
        for (const s of layout.slots) {
            m.set(s.ref.byPackageProductId, slotVisualBounds(s, layout));
        }
        return m;
    }, [layout]);
    // Projected screen-space quad of each slot in its *unhovered* position,
    // sorted back-to-front by z. Hover hit-testing runs against these stable
    // quads (not the live DOM geometry) so the hovered slot's grow transform —
    // which inflates it to ~75% of the viewport and would otherwise swallow
    // every neighbour's pointer events — doesn't lock hover onto it. Moving the
    // cursor over where a slot *sits in the base layout* hovers that slot, even
    // while another is enlarged on top. Memoised on [layout] so it only
    // recomputes when the layout actually changes, not on every hover render.
    const slotQuads = useMemo(() => [...layout.slots]
        .sort((a, b) => { var _a, _b; return ((_a = a.z) !== null && _a !== void 0 ? _a : 0) - ((_b = b.z) !== null && _b !== void 0 ? _b : 0); })
        .map((s) => ({
        productId: s.ref.byPackageProductId,
        quad: projectedSheetQuad(s, layout),
    })), [layout]);
    const animatedHover = useMemo(() => computeHoverEntry(animatedHoverIndex, visualBoundsByProductId, labelsById, templateLabelsById, vw, vh), [
        animatedHoverIndex,
        visualBoundsByProductId,
        labelsById,
        templateLabelsById,
        vw,
        vh,
    ]);
    const stickyHover = useMemo(() => computeHoverEntry(stickyHoverIndex, visualBoundsByProductId, labelsById, templateLabelsById, vw, vh), [
        stickyHoverIndex,
        visualBoundsByProductId,
        labelsById,
        templateLabelsById,
        vw,
        vh,
    ]);
    const animatedHoverInfo = animatedHover.info;
    const stickyHoverInfo = stickyHover.info;
    const stickyLabel = stickyHover.label;
    const stickySubLabel = stickyHover.subLabel;
    // Slots and decorations share one z stack so a decoration can sit in
    // front of one product and behind another. Build a single back-to-front
    // render order across both, then lift the hovered slot to the front.
    const decorations = (_b = layout.decorations) !== null && _b !== void 0 ? _b : [];
    const renderables = [
        ...layout.slots.map((s) => {
            var _a;
            return ({
                z: (_a = s.z) !== null && _a !== void 0 ? _a : 0,
                item: { kind: "slot", slot: s },
            });
        }),
        ...decorations.map((d) => {
            var _a;
            return ({
                z: (_a = d.z) !== null && _a !== void 0 ? _a : 0,
                item: { kind: "decoration", decoration: d },
            });
        }),
    ].sort((a, b) => a.z - b.z);
    const asItem = (r) => r.kind === "slot"
        ? { kind: "slot", slot: r.slot, hovered: false }
        : { kind: "decoration", decoration: r.decoration };
    const items = activeHover
        ? [
            ...renderables
                .filter((r) => !(r.item.kind === "slot" && r.item.slot === activeHover))
                .map((r) => asItem(r.item)),
            { kind: "slot", slot: activeHover, hovered: true },
        ]
        : renderables.map((r) => asItem(r.item));
    // Shared perspective: every leaning slot projects against the same
    // vanishing point so multiple sheets feel like one 3D scene on a shelf.
    // CSS native: set `perspective` + `perspective-origin` on the design-
    // viewport root. Per-slot mode (sharedPerspective off) puts each slot
    // in its own perspective context — implemented by zeroing out the
    // root's perspective and setting it per slot instead.
    const sharedPerspective = layout.sharedPerspective !== false;
    const perspectiveValue = layout.perspective || 1400;
    const perspectiveOrigin = layout.perspectiveOrigin;
    const backgroundCss = bg.type === "color"
        ? { background: bg.value }
        : bg.type === "image" && bg.url
            ? {
                backgroundImage: `url(${bg.url})`,
                backgroundSize: "cover",
                backgroundPosition: "center center",
                backgroundRepeat: "no-repeat",
            }
            : { background: "transparent" };
    // Resolve which slot the pointer is over from its position in the base
    // (unhovered) layout. Map client coords into design-viewport units via the
    // root's rect — the root fills the displayed design area exactly, so this
    // scale matches ScaledViewport's — then take the front-most slot whose
    // unhovered quad contains the point.
    const handleHoverMove = (e) => {
        const root = rootRef.current;
        if (!root)
            return;
        const r = root.getBoundingClientRect();
        if (r.width === 0 || r.height === 0)
            return;
        const px = ((e.clientX - r.left) * vw) / r.width;
        const py = ((e.clientY - r.top) * vh) / r.height;
        let hit = null;
        for (let i = slotQuads.length - 1; i >= 0; i--) {
            if (pointInQuad([px, py], slotQuads[i].quad)) {
                hit = slotQuads[i].productId;
                break;
            }
        }
        // Identical value short-circuits React's update, so firing this on every
        // mousemove costs nothing while the pointer stays within one slot.
        setHoveredProductId(hit);
    };
    // Outer aspect-ratio wrapper sizes itself to whatever width the consumer
    // gives us; the inner design-viewport div is the slot coordinate space
    // (1184×1184 by default), CSS-scaled to fit. transform-origin top-left
    // means slot.x/slot.y in layout units map cleanly to top/left CSS px
    // via the wrapper's containerQuery-style scale.
    return (React.createElement("div", { ref: rootRef, className: className, style: {
            width: "100%",
            aspectRatio: `${vw} / ${vh}`,
            display: "block",
            position: "relative",
        }, onMouseMove: enableHover ? handleHoverMove : undefined, onMouseLeave: enableHover ? () => setHoveredProductId(null) : undefined },
        React.createElement(DesignViewport, { vw: vw, vh: vh, backgroundCss: backgroundCss, sharedPerspective: sharedPerspective, perspectiveValue: perspectiveValue, perspectiveOrigin: perspectiveOrigin },
            items.map((item) => {
                var _a, _b, _c;
                if (item.kind === "decoration") {
                    const d = item.decoration;
                    return (React.createElement(DecorationLayer, { key: `deco-${d.id}`, decoration: d, interactive: interactiveDecorations !== null && interactiveDecorations !== void 0 ? interactiveDecorations : false, selected: selectedDecorationId !== undefined &&
                            selectedDecorationId !== null &&
                            selectedDecorationId === d.id }));
                }
                const slot = item.slot;
                const productId = slot.ref.byPackageProductId;
                const urls = (_a = photoUrlsById[productId]) !== null && _a !== void 0 ? _a : EMPTY_URLS;
                const cells = cellsForSlot(slot.defaultTemplateUuid, cellsByTemplateUuid);
                const cellTags = slot.defaultTemplateUuid
                    ? ((_b = cellTagsByTemplateUuid === null || cellTagsByTemplateUuid === void 0 ? void 0 : cellTagsByTemplateUuid[slot.defaultTemplateUuid]) !== null && _b !== void 0 ? _b : EMPTY_CELL_TAGS)
                    : EMPTY_CELL_TAGS;
                const userResources = (_c = templateUserResourcesById[productId]) !== null && _c !== void 0 ? _c : EMPTY_RESOURCES;
                const bounds = visualBoundsByProductId.get(productId);
                const isAnimatedHovered = animatedHoverIndex === productId;
                const transform = isAnimatedHovered && animatedHoverInfo
                    ? `translate(${animatedHoverInfo.dx.toFixed(2)}px, ${animatedHoverInfo.dy.toFixed(2)}px) scale(${animatedHoverInfo.scale.toFixed(4)})`
                    : "translate(0px, 0px) scale(1)";
                return (React.createElement("div", { key: `slot-${productId}`, style: enableHover && bounds
                        ? {
                            position: "absolute",
                            left: 0,
                            top: 0,
                            transformOrigin: `${bounds.centerX}px ${bounds.centerY}px`,
                            transform,
                            transition: `transform ${HOVER_TRANSITION_MS}ms ease-out`,
                            // Pass the design-viewport's 3D context through to
                            // the slot's own rotateX/Y. Without preserve-3d
                            // here the slot's 3D rotation gets flattened to 2D
                            // at this layer, breaking shared perspective.
                            transformStyle: "preserve-3d",
                        }
                        : {
                            position: "absolute",
                            left: 0,
                            top: 0,
                            transformStyle: "preserve-3d",
                        } },
                    React.createElement(Slot, { slot: slot, urls: urls, cells: cells, cellTags: cellTags, userResources: userResources, layout: layout, placeholders: placeholders, selected: selectedProductId !== undefined &&
                            selectedProductId !== null &&
                            selectedProductId === productId })));
            }),
            enableHover && (React.createElement("div", { style: {
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: `${vw}px`,
                    height: `${vh}px`,
                    pointerEvents: "none",
                    transform: `translate(${stickyHoverInfo
                        ? (stickyHoverInfo.centerX + stickyHoverInfo.dx).toFixed(2)
                        : (vw / 2).toFixed(2)}px, 0)`,
                    transition: `transform ${HOVER_TRANSITION_MS}ms ease-out`,
                } },
                React.createElement("div", { style: {
                        position: "absolute",
                        left: `-${vw / 2}px`,
                        top: `${(stickyHoverInfo
                            ? stickyHoverInfo.slotBottom + vh * HOVER_LABEL_GAP_OF_VH
                            : vh * 0.92).toFixed(2)}px`,
                        width: `${vw}px`,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        color: "#222",
                        fontFamily: HOVER_FONT_FAMILY,
                        opacity: animatedHoverIndex !== null ? 1 : 0,
                        transition: `opacity ${HOVER_TRANSITION_MS}ms ease-out, top ${HOVER_TRANSITION_MS}ms ease-out`,
                        pointerEvents: "none",
                    } },
                    React.createElement("div", { style: {
                            background: HOVER_LABEL_BG,
                            borderRadius: "9999px",
                            padding: `${(vh * HOVER_LABEL_PAD_Y_OF_VH).toFixed(2)}px ${(vh * HOVER_LABEL_PAD_X_OF_VH).toFixed(2)}px`,
                            whiteSpace: "nowrap",
                            fontSize: `${vh * HOVER_LABEL_FONT_OF_VH}px`,
                            lineHeight: 1,
                            fontWeight: 600,
                            textShadow: HOVER_LABEL_HALO,
                        } }, stickyLabel),
                    stickySubLabel && (React.createElement("div", { style: {
                            marginTop: `${vh * 0.008}px`,
                            background: HOVER_LABEL_BG,
                            borderRadius: "9999px",
                            padding: `${(vh * HOVER_LABEL_PAD_Y_OF_VH).toFixed(2)}px ${(vh * HOVER_LABEL_PAD_X_OF_VH).toFixed(2)}px`,
                            whiteSpace: "nowrap",
                            fontSize: `${vh * HOVER_SUBLABEL_FONT_OF_VH}px`,
                            lineHeight: 1,
                            fontWeight: 500,
                            textShadow: HOVER_LABEL_HALO,
                        } }, stickySubLabel))))),
            overlay && (React.createElement("svg", { className: "pg-overlay", viewBox: `0 0 ${vw} ${vh}`, xmlns: "http://www.w3.org/2000/svg", style: {
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: `${vw}px`,
                    height: `${vh}px`,
                    pointerEvents: "none",
                    overflow: "visible",
                } }, overlay)))));
}
// A free-floating decoration image. Flat 2D: positioned by its centre,
// scaled width/height already baked in, spun by a plain 2D rotation. Rendered
// as a plain <img> so PNG transparency comes through natively. Sits in the
// same z-ordered render list as slots. data-decoration-id lets the editor's
// hit-testing resolve clicks; the "selected" class drives the editor outline.
function DecorationLayer({ decoration, selected, interactive, }) {
    var _a;
    const d = decoration;
    return (React.createElement("img", { src: d.url, alt: "", draggable: false, "data-decoration-id": d.id, className: `pg-decoration${selected ? " selected" : ""}`, style: {
            position: "absolute",
            left: `${d.x - d.width / 2}px`,
            top: `${d.y - d.height / 2}px`,
            width: `${d.width}px`,
            height: `${d.height}px`,
            transformOrigin: "center center",
            transform: `rotate(${d.rotation}deg)${d.flipX ? " scaleX(-1)" : ""}`,
            opacity: (_a = d.opacity) !== null && _a !== void 0 ? _a : 1,
            // Interactive only in the editor (for hit-testing); off in the
            // webshop so a decoration can't swallow hover events from products
            // it overlaps.
            pointerEvents: interactive ? "auto" : "none",
            userSelect: "none",
        } }));
}
// Inner design-viewport: the slot coordinate space (1184×1184 by default).
// Sized in CSS px to match the layout dimensions, then proportionally
// scaled to fit the outer wrapper's width via a CSS resize-aware scale.
// Uses CSS perspective + perspective-origin to set up the shared 3D
// vanishing point that each leaning slot projects against (native browser
// 3D rendering, same model as projectedSheetQuad).
function DesignViewport({ vw, vh, backgroundCss, sharedPerspective, perspectiveValue, perspectiveOrigin, children, }) {
    // Use CSS calc + viewBox-style scale: the inner div is sized in layout
    // units (vw × vh px). The scale ratio comes from the outer wrapper's
    // current width via a CSS custom property updated by a ResizeObserver
    // — but for cheap, fall back to scale: var(--pg-scale, 1). We compute
    // the scale via a wrapping <div> that uses container queries indirectly
    // via the standard pattern: an outer 100% × auto wrapper holds an
    // absolutely-positioned inner div at the design size, scaled.
    return (React.createElement(ScaledViewport, { vw: vw, vh: vh },
        React.createElement("div", { className: "pg-design-viewport", style: Object.assign(Object.assign({ position: "relative", width: `${vw}px`, height: `${vh}px`, overflow: "hidden" }, backgroundCss), (sharedPerspective
                ? {
                    perspective: `${perspectiveValue}px`,
                    perspectiveOrigin: `${perspectiveOrigin.x}px ${perspectiveOrigin.y}px`,
                    transformStyle: "preserve-3d",
                }
                : {})) }, children)));
}
// Wraps the design viewport so it visually scales to the outer container's
// width while keeping the inner layout-unit coordinate space intact. Uses
// a ResizeObserver to update a CSS scale on the inner element. The outer
// wrapper has aspect-ratio so the consumer doesn't need to know about
// vw/vh.
function ScaledViewport({ vw, vh, children, }) {
    const outerRef = React.useRef(null);
    const innerRef = React.useRef(null);
    React.useLayoutEffect(() => {
        if (!outerRef.current || !innerRef.current)
            return;
        const apply = () => {
            if (!outerRef.current || !innerRef.current)
                return;
            const w = outerRef.current.clientWidth;
            const scale = w / vw;
            innerRef.current.style.transform = `scale(${scale})`;
        };
        apply();
        const ro = new ResizeObserver(apply);
        ro.observe(outerRef.current);
        return () => ro.disconnect();
    }, [vw]);
    return (React.createElement("div", { ref: outerRef, style: {
            position: "absolute",
            inset: 0,
            overflow: "hidden",
        } },
        React.createElement("div", { ref: innerRef, style: {
                position: "absolute",
                left: 0,
                top: 0,
                width: `${vw}px`,
                height: `${vh}px`,
                transformOrigin: "0 0",
            } }, children)));
}
function cellsForSlot(templateUuid, cellsByTemplateUuid) {
    if (templateUuid && cellsByTemplateUuid[templateUuid]) {
        return cellsByTemplateUuid[templateUuid];
    }
    return FULL_CELL;
}
function slotVisualBounds(slot, layout) {
    const quad = projectedSheetQuad(slot, layout);
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const [x, y] of quad) {
        if (x < minX)
            minX = x;
        if (x > maxX)
            maxX = x;
        if (y < minY)
            minY = y;
        if (y > maxY)
            maxY = y;
    }
    return {
        centerX: (minX + maxX) / 2,
        centerY: (minY + maxY) / 2,
        width: maxX - minX,
        height: maxY - minY,
    };
}
function computeHoverEntry(idx, visualBoundsByProductId, labelsById, templateLabelsById, vw, vh) {
    if (idx === null)
        return { info: null, label: "", subLabel: "" };
    const bounds = visualBoundsByProductId.get(idx);
    if (!bounds)
        return { info: null, label: "", subLabel: "" };
    const label = labelsById && labelsById[idx] !== undefined ? labelsById[idx] : "";
    const subLabel = templateLabelsById && templateLabelsById[idx] !== undefined
        ? templateLabelsById[idx]
        : "";
    return {
        info: computeHover(bounds, label, subLabel, vw, vh),
        label,
        subLabel,
    };
}
function computeHover(bounds, label, subLabel, vw, vh) {
    const w = Math.max(bounds.width, 1);
    const h = Math.max(bounds.height, 1);
    const originalLargest = Math.max(w, h);
    const targetMaxSide = 0.75 * vh;
    const hoveredLargest = 0.5 * originalLargest + 0.5 * targetMaxSide;
    const scale = hoveredLargest / originalLargest;
    const newH = h * scale;
    const newW = w * scale;
    let targetCy = 0.5 * bounds.centerY + 0.5 * 0.45 * vh;
    const maxBottom = 0.82 * vh;
    const bottom = targetCy + newH / 2;
    if (bottom > maxBottom) {
        targetCy = maxBottom - newH / 2;
    }
    const dy = targetCy - bounds.centerY;
    // Bottom edge of the slot's visual AABB after the scale+translate, used to
    // anchor the label below it.
    const slotBottom = targetCy + newH / 2;
    // Side-margin constraint uses the wider of the two label lines so the
    // whole stacked group stays inside the margins. The template line is set
    // at the smaller font size, so it's measured at that size.
    const fontSize = vh * HOVER_LABEL_FONT_OF_VH;
    const subFontSize = vh * HOVER_SUBLABEL_FONT_OF_VH;
    // Include the pill's horizontal padding (both sides) so the capsule, not
    // just the text, is what's kept inside the side margins.
    const textWidth = Math.max(measureTextWidth(label, `${fontSize}px ${HOVER_FONT_FAMILY}`), measureTextWidth(subLabel, `${subFontSize}px ${HOVER_FONT_FAMILY}`)) +
        2 * vh * HOVER_LABEL_PAD_X_OF_VH;
    const margin = vh * HOVER_MARGIN_OF_VH;
    const halfGroup = Math.max(newW, textWidth) / 2;
    const dxMin = margin + halfGroup - bounds.centerX;
    const dxMax = vw - margin - halfGroup - bounds.centerX;
    let dx;
    if (dxMin > dxMax) {
        dx = (dxMin + dxMax) / 2;
    }
    else {
        dx = Math.max(dxMin, Math.min(dxMax, 0));
    }
    return {
        scale,
        dy,
        dx,
        centerX: bounds.centerX,
        centerY: bounds.centerY,
        slotBottom,
    };
}
